/**
 * RESPONSIBILITY: say whether a CSS media query matches, and keep saying so as it changes.
 *
 * Read synchronously on the first render, not in an effect afterwards: the hero asks this
 * to choose which film to load, and an answer that arrived one frame late would have a
 * phone start downloading the large film before switching to its own.
 */
import { useEffect, useState } from 'react';

const matches = (query: string) =>
  typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia(query).matches
    : false;

export function useMediaQuery(query: string): boolean {
  const [on, setOn] = useState(() => matches(query));

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const mq = window.matchMedia(query);
    const read = () => setOn(mq.matches);
    read();
    mq.addEventListener('change', read);
    return () => mq.removeEventListener('change', read);
  }, [query]);

  return on;
}
